import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useVoice } from '@/context/VoiceContext';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ActivityLog } from '@/components/ActivityLog';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CalendarPlus, Search, MoreHorizontal, FileText, Trash2, Phone, Mail, MapPin, CalendarClock, Tag, Info, User, Plus, Navigation, Globe, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['all', 'new', 'contacted', 'quoted', 'lost'];

// Blue Well Drilling office — same reference point used across Estimates/Maps
// so the "distance" shown on a Lead matches the rest of the app.
const OFFICE_LOCATION = { lat: 27.4958, lng: -81.4411 };

function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Small self-contained map (office + lead pins) — lighter than the full
// WellMapVerification (no nearby-wells lookup needed here).
function LeadMiniMap({ lat, lng }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!containerRef.current || !lat || !lng) return;
      const L = await import('leaflet');
      if (cancelled || !containerRef.current) return;
      if (mapRef.current) { try { mapRef.current.remove(); } catch { /* noop */ } mapRef.current = null; }
      const map = L.map(containerRef.current, { zoomControl: true, fadeAnimation: false }).setView([lat, lng], 10);
      mapRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OSM' }).addTo(map);
      const officeIcon = L.divIcon({
        html: '<div style="background:#003366;width:24px;height:24px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>',
        iconSize: [24, 24], className: ''
      });
      const leadIcon = L.divIcon({
        html: '<div style="background:#dc2626;width:24px;height:24px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>',
        iconSize: [24, 24], className: ''
      });
      L.marker([OFFICE_LOCATION.lat, OFFICE_LOCATION.lng], { icon: officeIcon }).addTo(map).bindPopup('Blue Well Drilling — Oficina');
      L.marker([lat, lng], { icon: leadIcon }).addTo(map).bindPopup('Lead');
      const bounds = L.latLngBounds([[OFFICE_LOCATION.lat, OFFICE_LOCATION.lng], [lat, lng]]);
      map.fitBounds(bounds, { padding: [30, 30] });
      setTimeout(() => { try { map.invalidateSize(); } catch { /* noop */ } }, 150);
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) { try { mapRef.current.remove(); } catch { /* noop */ } mapRef.current = null; }
    };
  }, [lat, lng]);

  if (!lat || !lng) return null;
  return <div ref={containerRef} data-testid="lead-mini-map" className="h-[220px] w-full rounded-lg border border-gray-200 z-0" />;
}

const EMPTY_LEAD_FORM = {
  first_name: '', last_name: '', phone: '', email: '',
  address: '', city: '', county: '', state: 'FL', zip_code: '',
  lat: null, lng: null, parcel_id: '', owner_name: '',
  service_type: 'new_construction', description: '',
};

export default function BookingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailBooking, setDetailBooking] = useState(null);
  const { makeCall, device, callStatus } = useVoice();

  // Add Lead dialog state
  const [showAddLead, setShowAddLead] = useState(false);
  const [leadForm, setLeadForm] = useState(EMPTY_LEAD_FORM);
  const [leadDistance, setLeadDistance] = useState(null);
  const [savingLead, setSavingLead] = useState(false);
  const [lookingUpProperty, setLookingUpProperty] = useState(false);

  const normalizePhone = (raw) => {
    const digits = (raw || '').replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return raw?.startsWith('+') ? raw : `+${digits}`;
  };

  const handleCall = async (b) => {
    if (!b?.phone) return;
    if (!device) { toast.error('Voice service not available. Configure Twilio credentials.'); return; }
    if (callStatus !== 'idle') { toast.warning('Already on a call.'); return; }
    try {
      await makeCall(normalizePhone(b.phone), `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.phone);
    } catch (err) {
      toast.error(`Call failed: ${err.message}`);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get('/bookings', { params });
      setBookings(res.data);
    } catch { toast.error(t('bookings.errorLoading')); }
    finally { setLoading(false); }
  }, [statusFilter, search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMarkContacted = async (id) => {
    try { await api.put(`/bookings/${id}`, { status: 'contacted' }); toast.success(t('bookings.markedContacted')); fetchData(); }
    catch { toast.error(t('errors.generic')); }
  };

  const handleConvert = async (id) => {
    try {
      const res = await api.post(`/bookings/${id}/convert`);
      toast.success(t('bookings.convertedToEstimate', { number: res.data.estimate.estimate_number }));
      navigate('/estimates', { state: { openEstimateId: res.data.estimate.id } });
    } catch (err) { toast.error(err.response?.data?.detail || t('bookings.convertError')); }
  };

  const handleMarkLost = async (id) => {
    try { await api.put(`/bookings/${id}`, { status: 'lost' }); toast.success(t('bookings.markedLost')); fetchData(); }
    catch { toast.error(t('errors.generic')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/bookings/${id}`); toast.success(t('bookings.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('es-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  // ---- Add Lead dialog ----
  const openAddLead = () => {
    setLeadForm(EMPTY_LEAD_FORM);
    setLeadDistance(null);
    setShowAddLead(true);
  };

  const handleLeadAddressSelect = (data) => {
    const isTyping = data.lat === null;
    setLeadForm(prev => ({
      ...prev,
      address: data.address || prev.address,
      // Clear stale city/county/state/zip/coords from a PREVIOUS selection
      // while the user is still typing a new address (data.lat === null is
      // how AddressAutocomplete signals "not selected yet").
      city: isTyping ? '' : (data.city || prev.city),
      county: isTyping ? '' : (data.county || prev.county),
      state: isTyping ? '' : (data.state || prev.state || 'FL'),
      zip_code: isTyping ? '' : (data.zip_code || prev.zip_code),
      lat: isTyping ? null : (data.lat ?? prev.lat),
      lng: isTyping ? null : (data.lng ?? prev.lng),
    }));
    if (isTyping) {
      setLeadDistance(null);
      return;
    }
    if (data.lat && data.lng) {
      setLeadDistance(haversineMiles(OFFICE_LOCATION.lat, OFFICE_LOCATION.lng, data.lat, data.lng));
      // Best-effort owner/parcel lookup so "Convert to Estimate" can carry it along.
      setLookingUpProperty(true);
      api.get('/property/by-coordinates', { params: { lat: data.lat, lng: data.lng, county: data.county || '', address: data.address || '' } })
        .then(res => {
          const prop = res.data;
          if (prop && (prop.owner_name || prop.parcel_id)) {
            setLeadForm(prev => ({ ...prev, owner_name: prop.owner_name || prev.owner_name, parcel_id: prop.parcel_id || prev.parcel_id }));
          }
        })
        .catch(() => { /* silent — manual verification later is fine */ })
        .finally(() => setLookingUpProperty(false));
    } else {
      setLeadDistance(null);
    }
  };

  const handleCreateLead = async () => {
    if (!leadForm.address.trim()) { toast.error(t('bookings.addressRequiredError')); return; }
    setSavingLead(true);
    try {
      const payload = { ...leadForm, distance_miles: leadDistance };
      await api.post('/bookings', payload);
      toast.success(t('bookings.leadCreated'));
      setShowAddLead(false);
      setLeadForm(EMPTY_LEAD_FORM);
      setLeadDistance(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookings.leadCreateError'));
    } finally {
      setSavingLead(false);
    }
  };

  return (
    <div data-testid="bookings-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('bookings.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('bookings.totalBookings', { count: bookings.length })}</p>
        </div>
        <Button data-testid="add-lead-btn" onClick={openAddLead} className="bg-[#003366] hover:bg-[#004080] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> {t('bookings.addLead')}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-bookings" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('bookings.searchPlaceholder')} className="pl-10 h-9 bg-white" />
        </div>
        <div className="tabs-scroll">
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList className="bg-white border border-gray-200 h-9">
              {STATUSES.map(s => (
                <TabsTrigger key={s} value={s} data-testid={`booking-filter-${s}`} className="text-xs px-2 sm:px-2.5 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">
                  {t(`bookings.statuses.${s}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Desktop Table */}
      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : bookings.length === 0 ? (
            <div className="py-16 text-center">
              <CalendarPlus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('bookings.noBookings')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">#</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('bookings.tableHeaders.customer')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('bookings.tableHeaders.service')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">{t('common.address')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">{t('bookings.tableHeaders.source')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('bookings.tableHeaders.status')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('bookings.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => setDetailBooking(b)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer">
                      <td className="px-5 py-3.5 text-sm font-mono text-gray-500">{b.booking_number}</td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-gray-900">{(b.first_name || b.last_name) ? `${b.first_name} ${b.last_name}`.trim() : <span className="text-gray-400 italic">{t('bookings.nameOptional')}</span>}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {b.phone && <span className="text-xs text-gray-400 flex items-center gap-0.5"><Phone className="w-3 h-3" />{b.phone}</span>}
                          {b.email && <span className="text-xs text-gray-400 flex items-center gap-0.5"><Mail className="w-3 h-3" />{b.email}</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden md:table-cell">{t(`bookings.serviceLabels.${b.service_type}`, { defaultValue: b.service_type })}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden lg:table-cell">
                        <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" />{b.address ? `${b.address}${b.city ? ', ' + b.city : ''}` : `${b.city}${b.county ? `, ${b.county}` : ''}`}</div>
                        {typeof b.distance_miles === 'number' && (
                          <span className="text-[11px] text-gray-400">{b.distance_miles.toFixed(1)} mi {t('bookings.distanceFromOffice')}</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${b.source === 'manual' ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-sky-700'}`}>
                          {b.source === 'manual' ? <UserCog className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                          {b.source === 'manual' ? t('bookings.sourceManual') : t('bookings.sourceWebsite')}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={b.status} />
                        {b.status === 'quoted' && b.estimate_number && (
                          <button
                            type="button"
                            data-testid={`booking-estimate-link-${b.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate('/estimates', { state: { openEstimateId: b.estimate_id } }); }}
                            className="mt-1 block text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded-full transition-colors"
                          >
                            {b.estimate_number}
                          </button>
                        )}
                        {b.status === 'quoted' && b.estimate_link_broken && (
                          <span data-testid={`booking-estimate-broken-${b.id}`} className="mt-1 block text-xs text-gray-400 italic">
                            {t('bookings.estimateNotFound', { defaultValue: 'estimate not found' })}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {['new', 'contacted'].includes(b.status) && (
                            <Button data-testid={`convert-booking-btn-${b.id}`} variant="outline" size="sm" onClick={() => handleConvert(b.id)}
                              className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 text-xs h-7 px-2.5">
                              <FileText className="w-3 h-3 mr-1" /> {t('bookings.convertToEstimate')}
                            </Button>
                          )}
                          <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {b.phone && (
                              <DropdownMenuItem data-testid={`call-booking-${b.id}`} onClick={() => handleCall(b)}>
                                <Phone className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('bookings.call', { defaultValue: 'Call' })}
                              </DropdownMenuItem>
                            )}
                            {b.status === 'new' && (
                              <DropdownMenuItem onClick={() => handleMarkContacted(b.id)}>
                                <Phone className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('bookings.markContacted')}
                              </DropdownMenuItem>
                            )}
                            {['new', 'contacted'].includes(b.status) && (
                              <DropdownMenuItem data-testid={`convert-booking-${b.id}`} onClick={() => handleConvert(b.id)}>
                                <FileText className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('bookings.convertToEstimate')}
                              </DropdownMenuItem>
                            )}
                            {b.status !== 'lost' && (
                              <DropdownMenuItem data-testid={`mark-lost-${b.id}`} onClick={() => handleMarkLost(b.id)} className="text-rose-600">{t('bookings.markLost')}</DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDelete(b.id)} className="text-red-600">
                              <Trash2 className="w-3.5 h-3.5 mr-2" />{t('bookings.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : bookings.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <CalendarPlus className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('bookings.noBookings')}</p>
          </CardContent></Card>
        ) : (
          bookings.map((b, idx) => (
            <Card key={b.id} data-testid={`booking-mobile-${b.id}`} onClick={() => setDetailBooking(b)} className="bg-white border-gray-100 shadow-sm mobile-card-enter cursor-pointer" style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{(b.first_name || b.last_name) ? `${b.first_name} ${b.last_name}`.trim() : t('bookings.nameOptional')}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      <span className="font-mono">{b.booking_number}</span>
                      <span>{formatDate(b.created_at)}</span>
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {b.phone && <DropdownMenuItem data-testid={`call-booking-mobile-${b.id}`} onClick={() => handleCall(b)}><Phone className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('bookings.call', { defaultValue: 'Call' })}</DropdownMenuItem>}
                      {b.status === 'new' && <DropdownMenuItem onClick={() => handleMarkContacted(b.id)}><Phone className="w-3.5 h-3.5 mr-2" />{t('bookings.markContacted')}</DropdownMenuItem>}
                      {['new', 'contacted'].includes(b.status) && <DropdownMenuItem onClick={() => handleConvert(b.id)}><FileText className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('bookings.convertToEstimate')}</DropdownMenuItem>}
                      {b.status !== 'lost' && <DropdownMenuItem onClick={() => handleMarkLost(b.id)} className="text-rose-600">{t('bookings.markLost')}</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => handleDelete(b.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('bookings.delete')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge status={b.status} />
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-medium">{t(`bookings.serviceLabels.${b.service_type}`, { defaultValue: b.service_type })}</span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  {b.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{b.phone}</span>}
                  {b.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{b.email}</span>}
                </div>
                {['new', 'contacted'].includes(b.status) && (
                  <Button data-testid={`convert-mobile-${b.id}`} variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleConvert(b.id); }}
                    className="mt-3 w-full border-emerald-200 text-emerald-600 hover:bg-emerald-50 text-xs">
                    <FileText className="w-3 h-3 mr-1.5" /> {t('bookings.convertToEstimate')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Booking/Lead detail modal */}
      <Dialog open={!!detailBooking} onOpenChange={(o) => { if (!o) setDetailBooking(null); }}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
          {detailBooking && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Barlow'] flex items-center gap-2">
                  <CalendarPlus className="w-5 h-5 text-[#3399FF]" />
                  {(detailBooking.first_name || detailBooking.last_name) ? `${detailBooking.first_name} ${detailBooking.last_name}`.trim() : t('bookings.nameOptional')}
                </DialogTitle>
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs font-mono text-gray-500">{detailBooking.booking_number}</span>
                  <StatusBadge status={detailBooking.status} />
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${detailBooking.source === 'manual' ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-sky-700'}`}>
                    {detailBooking.source === 'manual' ? <UserCog className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    {detailBooking.source === 'manual' ? t('bookings.sourceManual') : t('bookings.sourceWebsite')}
                  </span>
                </div>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('common.phone', { defaultValue: 'Phone' })}</p>
                      <p className="text-sm text-gray-800" data-testid="booking-detail-phone">{detailBooking.phone || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('common.email', { defaultValue: 'Email' })}</p>
                      <p className="text-sm text-gray-800 truncate">{detailBooking.email || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('bookings.serviceType')}</p>
                      <p className="text-sm text-gray-800">{t(`bookings.serviceLabels.${detailBooking.service_type}`, { defaultValue: detailBooking.service_type || '—' })}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('estimates.owner', { defaultValue: 'Owner' })} / Parcel</p>
                      <p className="text-sm text-gray-800">{detailBooking.owner_name || '—'} {detailBooking.parcel_id ? `· ${detailBooking.parcel_id}` : ''}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('common.address', { defaultValue: 'Address' })}</p>
                    <p className="text-sm text-gray-800" data-testid="booking-detail-address">
                      {[detailBooking.address, detailBooking.city, detailBooking.county ? `${detailBooking.county} County` : '', detailBooking.state, detailBooking.zip_code]
                        .filter(Boolean).join(', ') || '—'}
                    </p>
                    {typeof detailBooking.distance_miles === 'number' && (
                      <p className="text-xs text-[#003366] font-semibold mt-1 flex items-center gap-1">
                        <Navigation className="w-3 h-3" /> {detailBooking.distance_miles.toFixed(1)} mi {t('bookings.distanceFromOffice')}
                      </p>
                    )}
                  </div>
                </div>

                {detailBooking.lat && detailBooking.lng && (
                  <LeadMiniMap lat={detailBooking.lat} lng={detailBooking.lng} />
                )}

                {detailBooking.how_heard && (
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('bookings.source', { defaultValue: 'How did you hear about us' })}</p>
                      <p className="text-sm text-gray-800">{detailBooking.how_heard}</p>
                    </div>
                  </div>
                )}

                {detailBooking.description && (
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('jobDetail.description', { defaultValue: 'Description' })}</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{detailBooking.description}</p>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-400 pt-1">{t('common.created', { defaultValue: 'Created' })}: {formatDate(detailBooking.created_at)}</p>

                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                  {detailBooking.phone && (
                    <Button data-testid="booking-detail-call-btn" size="sm" onClick={() => handleCall(detailBooking)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Phone className="w-3.5 h-3.5 mr-1.5" /> {t('bookings.call', { defaultValue: 'Call' })}
                    </Button>
                  )}
                  {['new', 'contacted'].includes(detailBooking.status) && (
                    <Button data-testid="booking-detail-convert-btn" size="sm" variant="outline" onClick={() => { handleConvert(detailBooking.id); setDetailBooking(null); }}
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50">
                      <FileText className="w-3.5 h-3.5 mr-1.5" /> {t('bookings.convertToEstimate')}
                    </Button>
                  )}
                  {detailBooking.status === 'new' && (
                    <Button size="sm" variant="outline" onClick={() => { handleMarkContacted(detailBooking.id); setDetailBooking(null); }}>
                      <Phone className="w-3.5 h-3.5 mr-1.5" /> {t('bookings.markContacted')}
                    </Button>
                  )}
                  {detailBooking.status !== 'lost' && (
                    <Button data-testid="booking-detail-lost-btn" size="sm" variant="outline" onClick={() => { handleMarkLost(detailBooking.id); setDetailBooking(null); }}
                      className="border-rose-200 text-rose-600 hover:bg-rose-50">
                      {t('bookings.markLost')}
                    </Button>
                  )}
                </div>

                {/* Activity Log (read-only) */}
                <ActivityLog entityType="bookings" entityId={detailBooking.id} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Lead dialog — manual entry, ADDRESS is mandatory, name is optional
          (Alian: "le llega la dirección y el teléfono, sin saber quién es el dueño"). */}
      <Dialog open={showAddLead} onOpenChange={setShowAddLead}>
        <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto" data-testid="add-lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#3399FF]" /> {t('bookings.addLeadTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1">
              <Label className="text-xs">{t('bookings.addressRequired')} *</Label>
              <AddressAutocomplete
                data-testid="lead-address-autocomplete"
                value={leadForm.address}
                onChange={handleLeadAddressSelect}
                placeholder="123 Main St, Punta Gorda..."
              />
              {leadDistance !== null && (
                <p className="text-xs text-[#003366] font-medium flex items-center gap-1 pt-0.5">
                  <Navigation className="w-3 h-3" /> {leadDistance.toFixed(1)} mi {t('bookings.distanceFromOffice')}
                </p>
              )}
              {lookingUpProperty && <p className="text-xs text-gray-400">{t('common.loading')}...</p>}
            </div>

            {leadForm.lat && leadForm.lng && <LeadMiniMap lat={leadForm.lat} lng={leadForm.lng} />}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t('bookings.nameOptional')} ({t('common.firstName', { defaultValue: 'First' })})</Label>
                <Input data-testid="lead-first-name-input" value={leadForm.first_name} onChange={e => setLeadForm({ ...leadForm, first_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('common.lastName', { defaultValue: 'Last' })}</Label>
                <Input data-testid="lead-last-name-input" value={leadForm.last_name} onChange={e => setLeadForm({ ...leadForm, last_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('common.phone', { defaultValue: 'Phone' })}</Label>
                <Input data-testid="lead-phone-input" value={leadForm.phone} onChange={e => setLeadForm({ ...leadForm, phone: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('common.email', { defaultValue: 'Email' })}</Label>
                <Input data-testid="lead-email-input" value={leadForm.email} onChange={e => setLeadForm({ ...leadForm, email: e.target.value })} />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">{t('bookings.serviceType')}</Label>
              <Select value={leadForm.service_type} onValueChange={v => setLeadForm({ ...leadForm, service_type: v })}>
                <SelectTrigger data-testid="lead-service-type-select" className="h-9 bg-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_construction">{t('bookings.serviceLabels.new_construction')}</SelectItem>
                  <SelectItem value="repair">{t('bookings.serviceLabels.repair')}</SelectItem>
                  <SelectItem value="pump_service">{t('bookings.serviceLabels.pump_service')}</SelectItem>
                  <SelectItem value="modification">{t('bookings.serviceLabels.modification')}</SelectItem>
                  <SelectItem value="abandonment">{t('bookings.serviceLabels.abandonment')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">{t('jobDetail.description', { defaultValue: 'Description' })}</Label>
              <Textarea data-testid="lead-description-input" value={leadForm.description} onChange={e => setLeadForm({ ...leadForm, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLead(false)} data-testid="lead-cancel-btn">{t('common.cancel')}</Button>
            <Button onClick={handleCreateLead} disabled={savingLead || !leadForm.address.trim()} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="lead-save-btn">
              {savingLead ? t('common.saving', { defaultValue: 'Saving...' }) : t('common.save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
